// src/utils/analytics.js
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '../config/firebase';

/**
 * Analytics utility for tracking user events and behaviors
 * Follows Firebase Analytics best practices
 */

// Event Categories
export const EventCategory = {
  AUTH: 'auth',
  GAME: 'game',
  SOCIAL: 'social',
  ENGAGEMENT: 'engagement',
  ERROR: 'error'
};

// Custom Event Names (following Firebase naming conventions)
export const EventName = {
  // Authentication Events
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  SIGNIN_COMPLETED: 'signin_completed',
  SIGNOUT: 'signout',
  EMAIL_VERIFICATION_SENT: 'email_verification_sent',
  EMAIL_VERIFIED: 'email_verified',

  // Game Events
  GAME_STARTED: 'game_started',
  GAME_COMPLETED: 'game_completed',
  GAME_WON: 'game_won',
  GAME_LOST: 'game_lost',
  GUESS_SUBMITTED: 'guess_submitted',
  HINT_USED: 'hint_used',
  GAME_SHARED: 'game_shared',

  // Leaderboard Events
  LEADERBOARD_VIEWED: 'leaderboard_viewed',
  LEADERBOARD_FILTER_CHANGED: 'leaderboard_filter_changed',
  PROFILE_VIEWED: 'profile_viewed',

  // Engagement Events
  PAGE_VIEW: 'page_view',
  STATS_VIEWED: 'stats_viewed',
  STREAK_MILESTONE: 'streak_milestone',
  SCORE_MILESTONE: 'score_milestone',

  // Error Events
  ERROR_OCCURRED: 'error_occurred',
  AUTH_ERROR: 'auth_error',
  GAME_ERROR: 'game_error'
};

/**
 * Logs an analytics event
 * @param {string} eventName - Name of the event
 * @param {Object} params - Event parameters
 */
export const logAnalyticsEvent = (eventName, params = {}) => {
  if (!analytics) {
    console.warn('Analytics not initialized');
    return;
  }

  try {
    logEvent(analytics, eventName, {
      timestamp: new Date().toISOString(),
      ...params
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Analytics Event:', eventName, params);
    }
  } catch (error) {
    console.error('Analytics logging error:', error);
  }
};

/**
 * Sets the user ID for analytics tracking
 * @param {string} userId - Firebase user ID
 */
export const setAnalyticsUserId = (userId) => {
  if (!analytics || !userId) return;

  try {
    setUserId(analytics, userId);
    console.log('✅ Analytics User ID set');
  } catch (error) {
    console.error('Error setting analytics user ID:', error);
  }
};

/**
 * Sets user properties for analytics
 * @param {Object} properties - User properties
 */
export const setAnalyticsUserProperties = (properties) => {
  if (!analytics || !properties) return;

  try {
    setUserProperties(analytics, properties);

    if (process.env.NODE_ENV === 'development') {
      console.log('📊 User Properties Set:', properties);
    }
  } catch (error) {
    console.error('Error setting user properties:', error);
  }
};

// ========== Authentication Analytics ==========

export const trackSignupStarted = (method = 'email') => {
  logAnalyticsEvent(EventName.SIGNUP_STARTED, {
    category: EventCategory.AUTH,
    method
  });
};

export const trackSignupCompleted = (userId, username) => {
  logAnalyticsEvent(EventName.SIGNUP_COMPLETED, {
    category: EventCategory.AUTH,
    method: 'email'
  });

  // Set user ID and initial properties
  setAnalyticsUserId(userId);
  setAnalyticsUserProperties({
    username: username,
    account_created: new Date().toISOString()
  });
};

export const trackSigninCompleted = (userId, method = 'email') => {
  logAnalyticsEvent(EventName.SIGNIN_COMPLETED, {
    category: EventCategory.AUTH,
    method
  });

  setAnalyticsUserId(userId);
};

export const trackSignout = () => {
  logAnalyticsEvent(EventName.SIGNOUT, {
    category: EventCategory.AUTH
  });
};

export const trackEmailVerificationSent = () => {
  logAnalyticsEvent(EventName.EMAIL_VERIFICATION_SENT, {
    category: EventCategory.AUTH
  });
};

export const trackEmailVerified = (userId) => {
  logAnalyticsEvent(EventName.EMAIL_VERIFIED, {
    category: EventCategory.AUTH
  });

  setAnalyticsUserProperties({
    email_verified: true
  });
};

// ========== Game Analytics ==========

export const trackGameStarted = (gameDate, attemptNumber = 1) => {
  logAnalyticsEvent(EventName.GAME_STARTED, {
    category: EventCategory.GAME,
    game_date: gameDate,
    attempt_number: attemptNumber
  });
};

export const trackGameCompleted = ({ gameDate, won, attempts, score, timeSpent, wordLength }) => {
  const eventName = won ? EventName.GAME_WON : EventName.GAME_LOST;

  logAnalyticsEvent(eventName, {
    category: EventCategory.GAME,
    game_date: gameDate,
    attempts: attempts,
    score: score,
    time_spent_seconds: timeSpent,
    word_length: wordLength,
    success: won
  });

  // Also log generic game_completed event
  logAnalyticsEvent(EventName.GAME_COMPLETED, {
    category: EventCategory.GAME,
    game_date: gameDate,
    result: won ? 'win' : 'loss',
    attempts: attempts,
    score: score
  });
};

export const trackGuessSubmitted = (attemptNumber, correctLetters, correctPositions) => {
  logAnalyticsEvent(EventName.GUESS_SUBMITTED, {
    category: EventCategory.GAME,
    attempt_number: attemptNumber,
    correct_letters: correctLetters,
    correct_positions: correctPositions
  });
};

export const trackGameShared = (platform) => {
  logAnalyticsEvent(EventName.GAME_SHARED, {
    category: EventCategory.SOCIAL,
    platform: platform
  });
};

// ========== Leaderboard Analytics ==========

export const trackLeaderboardViewed = (filterType = 'all') => {
  logAnalyticsEvent(EventName.LEADERBOARD_VIEWED, {
    category: EventCategory.ENGAGEMENT,
    filter_type: filterType
  });
};

export const trackLeaderboardFilterChanged = (fromFilter, toFilter) => {
  logAnalyticsEvent(EventName.LEADERBOARD_FILTER_CHANGED, {
    category: EventCategory.ENGAGEMENT,
    from_filter: fromFilter,
    to_filter: toFilter
  });
};

export const trackProfileViewed = (viewedUsername, isOwnProfile) => {
  logAnalyticsEvent(EventName.PROFILE_VIEWED, {
    category: EventCategory.ENGAGEMENT,
    is_own_profile: isOwnProfile
  });
};

// ========== Engagement Analytics ==========

export const trackPageView = (pageName, pageParams = {}) => {
  logAnalyticsEvent(EventName.PAGE_VIEW, {
    category: EventCategory.ENGAGEMENT,
    page_name: pageName,
    ...pageParams
  });
};

export const trackStatsViewed = () => {
  logAnalyticsEvent(EventName.STATS_VIEWED, {
    category: EventCategory.ENGAGEMENT
  });
};

export const trackStreakMilestone = (streakCount) => {
  logAnalyticsEvent(EventName.STREAK_MILESTONE, {
    category: EventCategory.ENGAGEMENT,
    streak_count: streakCount
  });
};

export const trackScoreMilestone = (totalScore, milestone) => {
  logAnalyticsEvent(EventName.SCORE_MILESTONE, {
    category: EventCategory.ENGAGEMENT,
    total_score: totalScore,
    milestone: milestone
  });
};

// ========== Error Analytics ==========

export const trackError = (errorType, errorMessage, context = {}) => {
  logAnalyticsEvent(EventName.ERROR_OCCURRED, {
    category: EventCategory.ERROR,
    error_type: errorType,
    error_message: errorMessage,
    ...context
  });
};

export const trackAuthError = (errorCode, errorMessage) => {
  logAnalyticsEvent(EventName.AUTH_ERROR, {
    category: EventCategory.ERROR,
    error_code: errorCode,
    error_message: errorMessage
  });
};

export const trackGameError = (errorType, errorMessage) => {
  logAnalyticsEvent(EventName.GAME_ERROR, {
    category: EventCategory.ERROR,
    error_type: errorType,
    error_message: errorMessage
  });
};

// ========== User Properties Helpers ==========

export const updateUserStats = (stats) => {
  if (!stats) return;

  setAnalyticsUserProperties({
    games_played: stats.gamesPlayed || 0,
    current_streak: stats.currentStreak || 0,
    max_streak: stats.maxStreak || 0,
    average_score: stats.avgScore || 0,
    total_score: stats.totalScore || 0,
    win_rate: stats.winRate || 0
  });
};

export const updateUserEngagement = (engagementData) => {
  setAnalyticsUserProperties({
    last_active: new Date().toISOString(),
    ...engagementData
  });
};
